import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldLabel,
  getFieldValue,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { FormInput, FormTextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function OHWWKQAR() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram.value = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.value) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          hideHeaderOnPrint
          letterhead={
            <div className="container border-bottom border-danger border-2 pb-2">
              <div className="row justify-content-center">
                <div className="col-auto">
                  <img src={logo} alt="wendy's wonderful kids logo"></img>
                </div>
              </div>
              <div className="row justify-content-center">
                <div className="col-auto fs-2 fw-bold">
                  Initial Adoption Readiness Assessment Template
                </div>
              </div>
            </div>
          }
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark d-print-none">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {getFieldValue(data.SelectedProgram) && (
            <>
              <div className="container border-top">
                <div className="row border-start border-bottom">
                  <div className="col border-end fw-bold p-1 pb-3">
                    Child's name
                  </div>
                  <div className="col border-end p-1 pb-3">
                    {clientData.client?.fullName}
                  </div>
                </div>
                <div className="row border-start border-bottom">
                  <div className="col border-end fw-bold p-1 pb-3">
                    {getFieldLabel(formConfig.CompletedBy)}
                  </div>
                  <div className="col border-end p-1 pb-3">
                    <FormInput
                      name="CompletedBy"
                      error={errors.CompletedBy}
                      value={getFieldValue(data.CompletedBy)}
                      onChange={handleChange}
                      identical={compare("CompletedBy")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start border-bottom">
                  <div className="col border-end fw-bold p-1 pb-3">
                    {getFieldLabel(formConfig.Date)}
                  </div>
                  <div className="col border-end p-1 pb-3">
                    <FormInput
                      name="Date"
                      error={errors.Date}
                      value={getFieldValue(data.Date)}
                      onChange={handleChange}
                      identical={compare("Date")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
              </div>
              <div className="ms-4 mt-4">
                <div className="fw-bold mb-2">
                  {getFieldLabel(formConfig.Whole)}
                </div>
                <FormTextArea
                  name="Description"
                  label={getFieldLabel(formConfig.Description)}
                  error={errors.Description}
                  value={getFieldValue(data.Description)}
                  onChange={handleChange}
                  identical={compare("Description")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 mb-4"
                />
                <FormTextArea
                  name="Strengths"
                  label={getFieldLabel(formConfig.Strengths)}
                  error={errors.Strengths}
                  value={getFieldValue(data.Strengths)}
                  onChange={handleChange}
                  identical={compare("Strengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Challenges"
                  label={getFieldLabel(formConfig.Challenges)}
                  error={errors.Challenges}
                  value={getFieldValue(data.Challenges)}
                  onChange={handleChange}
                  identical={compare("Challenges")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Education"
                  label={getFieldLabel(formConfig.Education)}
                  error={errors.Education}
                  value={getFieldValue(data.Education)}
                  onChange={handleChange}
                  identical={compare("Education")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Social"
                  label={getFieldLabel(formConfig.Social)}
                  error={errors.Social}
                  value={getFieldValue(data.Social)}
                  onChange={handleChange}
                  identical={compare("Social")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Medical"
                  label={getFieldLabel(formConfig.Medical)}
                  error={errors.Medical}
                  value={getFieldValue(data.Medical)}
                  onChange={handleChange}
                  identical={compare("Medical")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Psych"
                  label={getFieldLabel(formConfig.Psych)}
                  error={errors.Psych}
                  value={getFieldValue(data.Psych)}
                  onChange={handleChange}
                  identical={compare("Psych")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Family"
                  label={getFieldLabel(formConfig.Family)}
                  error={errors.Family}
                  value={getFieldValue(data.Family)}
                  onChange={handleChange}
                  identical={compare("Family")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Placement"
                  label={getFieldLabel(formConfig.Placement)}
                  error={errors.Placement}
                  value={getFieldValue(data.Placement)}
                  onChange={handleChange}
                  identical={compare("Placement")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
                <FormTextArea
                  name="Other"
                  label={getFieldLabel(formConfig.Other)}
                  error={errors.Other}
                  value={getFieldValue(data.Other)}
                  onChange={handleChange}
                  identical={compare("Other")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  labelClassName="fw-bold"
                  divClassName="ms-4 my-4"
                />
              </div>
              <FormTextArea
                name="Understanding"
                label={getFieldLabel(formConfig.Understanding)}
                error={errors.Understanding}
                value={getFieldValue(data.Understanding)}
                onChange={handleChange}
                identical={compare("Understanding")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                labelClassName="fw-bold"
                divClassName="ms-4 my-4"
              />
              <FormTextArea
                name="Feelings"
                label={getFieldLabel(formConfig.Feelings)}
                error={errors.Feelings}
                value={getFieldValue(data.Feelings)}
                onChange={handleChange}
                identical={compare("Feelings")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                labelClassName="fw-bold"
                divClassName="ms-4 my-4"
              />
              <FormTextArea
                name="Plan"
                label={getFieldLabel(formConfig.Plan)}
                error={errors.Plan}
                value={getFieldValue(data.Plan)}
                onChange={handleChange}
                identical={compare("Plan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                labelClassName="fw-bold"
                divClassName="ms-4 my-4"
              />
            </>
          )}
          <div className="d-print-none">
            <div
              className="btn btn-primary m-2"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
          <footer>
            ©2019 Dave Thomas Foundation for Adoption. This document is intended
            for the exclusive use of recruiters participating in the Wendy's
            Wonderful Kids program. All other uses are prohibited without the
            express permission of the Dave Thomas Foundation for Adoption.
          </footer>
        </div>
      </div>
    </>
  );
}

export default OHWWKQAR;

const logo =
  "data:image/png;base64,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";
